import React from 'react';
import { Paper, Grid, Typography, Box, LinearProgress, Chip } from '@mui/material';
import { Student } from '@/types';

interface AttendanceOverviewProps {
  students: Student[];
}

export const AttendanceOverview: React.FC<AttendanceOverviewProps> = ({ students }) => {
  const totalStudents = students.length;
  const averageAttendance = students.reduce((sum, student) => sum + student.attendanceRate, 0) / totalStudents;
  const presentToday = 93; // Fixed to 93 students present today
  const chronicAbsences = students.filter(s => s.attendanceRate < 90).length;

  const getAttendanceColor = (rate: number) => {
    if (rate >= 95) return 'success';
    if (rate >= 90) return 'warning';
    return 'error';
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        School-Wide Attendance Overview
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Average Attendance Rate
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="h4">
                {averageAttendance.toFixed(1)}%
              </Typography>
              <Chip
                label={averageAttendance >= 95 ? 'Excellent' : averageAttendance >= 90 ? 'Good' : 'Needs Improvement'}
                color={getAttendanceColor(averageAttendance)}
                size="small"
              />
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={averageAttendance}
            color={getAttendanceColor(averageAttendance)}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary">
            Present Today
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
            <Typography variant="h4">
              {presentToday}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              / {totalStudents} students
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {((presentToday / totalStudents) * 100).toFixed(1)}% attendance today
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary">
            Chronic Absences
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
            <Typography variant="h4">
              {chronicAbsences}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              students
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {((chronicAbsences / totalStudents) * 100).toFixed(1)}% of student body
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary">
            Total Enrollment
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1 }}>
            <Typography variant="h4">
              {totalStudents}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              students
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Across all grades
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};